/*
 * Field Mode's device store: the specimens photographed on this phone, with
 * their pictures, until the server has every one of them.
 *
 * ────────────────────────────────────────────────────────────────────────
 * A BATCH IS ONE SPECIMEN
 * ────────────────────────────────────────────────────────────────────────
 *
 * The camera fills an ACTIVE batch as photos are snapped. "New specimen"
 * finishes it (status `pending`) and the outbox (utils/fieldOutbox.js) mints
 * its code, creates the collection, uploads the photos and the voice notes
 * and submits it, saving what it has made here after every step. A batch that
 * needs a person goes to `attention`; one the server has in full is `sent`.
 *
 *   active -> pending -> sending -> sent
 *                     \-> attention -> (edited) -> pending
 *
 * ────────────────────────────────────────────────────────────────────────
 * NOT THE SYNC QUEUE, AND NOT A DRAFT IN localStorage
 * ────────────────────────────────────────────────────────────────────────
 *
 * utils/offlineQueue.js holds table rows for /sync. A photograph is not a
 * row: it is a blob that has to go through presign and storage, so a capture
 * queued there arrived without its pictures. This database holds the blobs
 * themselves, and is its own (`pitriot-field`), so clearing one never touches
 * the other. localStorage could not hold a day of 2 MB photographs at all.
 *
 * ────────────────────────────────────────────────────────────────────────
 * EVERYTHING BELONGS TO ONE PERSON
 * ────────────────────────────────────────────────────────────────────────
 *
 * Every batch and every blob carries the signed-in person's id, and every
 * read takes it. A phone passed between two collectors shows each only their
 * own specimens, and the outbox sends only the signed-in person's, under
 * their own token.
 *
 * ────────────────────────────────────────────────────────────────────────
 * THE BACKEND IS INJECTABLE
 * ────────────────────────────────────────────────────────────────────────
 *
 * As in offlineQueue.js: jsdom has no IndexedDB, and fake-indexeddb is not
 * worth shipping to test this. The browser passes IndexedDB, the tests pass
 * Maps. Unlike the sync queue, a failed WRITE is not swallowed here: a photo
 * that could not be kept is one the person has to be told about.
 *
 * An IndexedDB that exists but will not open (site data blocked, some
 * private windows) is found out on first use, and the store carries on in
 * memory for the page's life -- saying so (`persistent` false), rather than
 * claiming to keep photos it drops.
 *
 * ────────────────────────────────────────────────────────────────────────
 * ONE WRITER AT A TIME, ACROSS TABS TOO
 * ────────────────────────────────────────────────────────────────────────
 *
 * update() reads and writes a batch inside ONE transaction, so a change made
 * from what is stored now cannot be overwritten by a tab that read it a
 * moment earlier. The outbox claims a batch that way before sending it
 * (`sendingBy`, `leaseUntil`; see fieldOutbox.js), and a batch another tab is
 * sending is left alone until its lease runs out.
 */
import { newUuid } from './collectionRecord';
import { localIsoWithOffset } from './specimenCode';
import { UNOWNED_DRAFT_KEY } from './unownedDraft';
import { announceReadOnly, readOnlyError, readOnlyNow } from './viewAs';

const DB_NAME = 'pitriot-field';
const DB_VERSION = 1;
export const BATCHES = 'batches';
export const BLOBS = 'blobs';

/** How long a GPS fix still says where the phone is. */
export const FIX_MAX_AGE_MS = 2 * 60 * 1000;

// The views the camera files a photo under, in the order it offers them.
// Each is the photo_type it is uploaded as.
export const MAIN_VIEWS = ['cap_top', 'fertile_layer', 'habitat'];
export const CAMERA_VIEWS = [...MAIN_VIEWS, 'other'];
// What else a photo can be filed as afterwards, in the editor.
export const ALL_VIEWS = [...MAIN_VIEWS, 'stipe', 'stipe_base', 'cross_section', 'spore_print', 'other'];

// A record as IndexedDB would hand it back: a copy, never the object that
// was stored. Blobs are kept as they are (a clone of jsdom's Blob throws).
const copy = (value) => (typeof structuredClone === 'function'
  ? structuredClone(value)
  : JSON.parse(JSON.stringify(value)));

/** Maps behind the backend interface, for tests and for a browser with no IDB. */
export function memoryBackend() {
  const stores = { [BATCHES]: new Map(), [BLOBS]: new Map() };
  const out = (store, value) => (store === BLOBS ? value : copy(value));
  return {
    persistent: false,
    async ready() { return false; },
    async get(store, id) {
      return stores[store].has(id) ? out(store, stores[store].get(id)) : null;
    },
    async getAll(store) { return [...stores[store].values()].map(v => out(store, v)); },
    async put(store, entry) { stores[store].set(entry.id, out(store, entry)); return entry; },
    async remove(store, ids) { ids.forEach(id => stores[store].delete(id)); },
    /** Read, change and write one entry with nothing in between. `change`
     *  answers {put, value}: the entry to store (if any) and what to resolve. */
    async update(store, id, change) {
      const current = stores[store].has(id) ? out(store, stores[store].get(id)) : null;
      const { put, value } = change(current);
      if (put) stores[store].set(put.id, out(store, put));
      return value;
    },
  };
}

/** IndexedDB. With none at all (an old browser, some private modes), or one
 *  that will not open, the store still works for as long as the page is
 *  open, and says so. */
export function indexedDbBackend(factory = (typeof indexedDB !== 'undefined' ? indexedDB : null)) {
  if (!factory) return memoryBackend();

  let fallback = null;   // the Maps used once IndexedDB has refused to open
  let opened = null;
  const open = () => {
    opened = opened || new Promise((resolve, reject) => {
      let request;
      try {
        request = factory.open(DB_NAME, DB_VERSION);
      } catch (e) {
        reject(e);
        return;
      }
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(BATCHES)) db.createObjectStore(BATCHES, { keyPath: 'id' });
        if (!db.objectStoreNames.contains(BLOBS)) db.createObjectStore(BLOBS, { keyPath: 'id' });
      };
      request.onsuccess = () => {
        const db = request.result;
        // A browser may close the connection under the page (WebKit does,
        // after the tab sat in the background); the next use opens again.
        db.onclose = () => { opened = null; };
        db.onversionchange = () => { db.close(); opened = null; };
        resolve(db);
      };
      request.onerror = () => { opened = null; reject(request.error); };
    });
    return opened;
  };

  /* The first use finds out whether IndexedDB opens at all. If it does not,
   * every later call goes to the Maps instead. */
  let probe = null;
  const ready = () => {
    probe = probe || open().then(() => true, () => {
      fallback = memoryBackend();
      return false;
    });
    return probe;
  };

  const transaction = (store, mode, attempt = 0) => open().then((db) => {
    try {
      return db.transaction(store, mode);
    } catch (e) {
      // InvalidStateError: the connection was closed. Once more, afresh.
      opened = null;
      if (attempt > 0) throw e;
      return transaction(store, mode, attempt + 1);
    }
  });

  const run = (store, mode, work) => transaction(store, mode).then(tx => new Promise((resolve, reject) => {
    const request = work(tx.objectStore(store));
    tx.oncomplete = () => resolve(request && 'result' in request ? request.result : undefined);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  }));

  const via = (method, idb) => async (...args) => (
    (await ready()) ? idb(...args) : fallback[method](...args));

  return {
    get persistent() { return !fallback; },
    ready,
    get: via('get', (store, id) => run(store, 'readonly', s => s.get(id)).then(v => v ?? null)),
    getAll: via('getAll', store => run(store, 'readonly', s => s.getAll())),
    put: via('put', (store, entry) => run(store, 'readwrite', s => s.put(entry)).then(() => entry)),
    remove: via('remove', (store, ids) => run(store, 'readwrite', (s) => { ids.forEach(id => s.delete(id)); })),
    update: via('update', (store, id, change) => transaction(store, 'readwrite').then(tx => new Promise((resolve, reject) => {
      const objects = tx.objectStore(store);
      let value;
      const read = objects.get(id);
      read.onsuccess = () => {
        try {
          const answer = change(read.result ?? null);
          value = answer.value;
          if (answer.put) objects.put(answer.put);
        } catch (e) {
          reject(e);
          try { tx.abort(); } catch { /* already finishing */ }
        }
      };
      tx.oncomplete = () => resolve(value);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    }))),
  };
}

/** Whether a batch is being sent by somebody right now: its lease has not run
 *  out (fieldOutbox.js claims a batch with one, and renews it as it goes). */
export function leaseActive(batch, now = Date.now()) {
  if (batch?.status !== 'sending') return false;
  const until = Date.parse(batch.leaseUntil || '');
  return Number.isFinite(until) && until > now;
}

/** Whether this batch's code, date and place are settled: a specimen code was
 *  minted for it, or a mint went out that may have made one. Its record has
 *  to say what that code says, so they no longer change here. */
export const codeFixed = batch => !!batch?.specimenCode || (!!batch?.mintRequest && !batch?.mintRefused);

const sameName = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/**
 * The batch with the AI's answer as the camera's post-capture summary keeps
 * it (components/field/FieldCaptureSummary.js): `meta` is the batch's note of
 * the answer ({aiId, modelVersion, top, accepted, sawAiFirst, mine}, from
 * AiFieldSuggestion's answerOf), `accepted` the name chosen from it, or null.
 *
 * A name taken from the AI goes with the choice that made it: a new answer,
 * another pick or "none of these" takes it away, while a name the person typed
 * is never touched. A chosen name fills the details the record is created
 * from, so the outbox's aiLinkBody sends it as the link's `accept` (an
 * 'ai_image' identification) and createBody as current_taxon_name. A new
 * answer is linked afresh (aiLinked false).
 *
 * Null for a batch no longer waiting on this phone -- being sent, sent, or its
 * record made -- where an answer would never be linked.
 */
export function withAiAnswer(batch, meta, { holdUntil } = {}) {
  if (!batch || batch.status !== 'pending' || batch.serverSampleId) return null;
  const before = batch.ai || null;
  const same = !!before?.aiId && before.aiId === meta?.aiId;
  return {
    ...batch,
    ai: meta || null,
    aiLinked: same ? !!batch.aiLinked : false,
    details: detailsForAiAnswer(batch.details, before, meta),
    ...(holdUntil !== undefined ? { holdUntil } : {}),
  };
}

/**
 * The batch with a pickup or drop-off asked for (B6; utils/handoffs.js
 * cleanRequest), or none (`request` null), or null when it is too late: the
 * batch is already sent, and the request goes to the server from its record
 * instead. The outbox sends it once the record is submitted
 * (fieldOutbox.js, the Handoff step); `aiId` is the answer that prompted it,
 * when the live prompt did. Renews the hold like withAiAnswer.
 *
 * Each request stored gets an idempotency key of its own (`handoffKey`), so
 * a request changed after an earlier one was answered never goes under that
 * earlier one's key (the server would answer it 422).
 */
export function withHandoffRequest(batch, request, { aiId = null, holdUntil } = {}) {
  if (!batch || batch.status === 'sent') return null;
  return {
    ...batch,
    handoff: request ? { ...request, aiId: aiId || null, askedAt: new Date().toISOString() } : null,
    handoffKey: request ? `field-${newUuid()}` : null,
    handoffSent: false,
    handoffRefused: null,
    ...(holdUntil !== undefined ? { holdUntil } : {}),
  };
}

/**
 * The details as the AI's answer `meta` leaves them, after `before` (the
 * answer they were filled from, or null): a name taken from the AI goes with
 * the choice that made it -- a new answer, another pick or "none of these"
 * takes it away, a name the person typed stays -- and a chosen name fills
 * name, rank and confidence (`uncertain` unless already said). The camera's
 * summary (withAiAnswer) and the batch editor apply the same rule, so a name
 * on the record from the AI always goes with its answer's `accept`.
 */
export function detailsForAiAnswer(details, before, meta) {
  const next = { ...(details || {}) };
  const same = !!before?.aiId && before.aiId === meta?.aiId;
  const keptPick = same && sameName(meta?.accepted?.taxon_name, before?.accepted?.taxon_name);
  if (before?.accepted && sameName(next.taxon_name, before.accepted.taxon_name) && !keptPick) {
    delete next.taxon_name;
    delete next.rank;
    delete next.confidence;
  }
  if (meta?.accepted) {
    Object.assign(next, {
      taxon_name: meta.accepted.taxon_name,
      rank: meta.accepted.rank,
      confidence: next.confidence || 'uncertain',
    });
  }
  return next;
}

/** Whether this name is the one the AI's answer `ai` was picked for. */
export const isAiPickedName = (name, ai) => !!ai?.accepted && sameName(name, ai.accepted.taxon_name);

const order = b => String(b.startedAt || b.createdAt || '');

export function createBatchStore(backend = indexedDbBackend(), { viewing = () => readOnlyNow() } = {}) {
  // Nothing is kept here while an admin views the app as another role: a
  // batch made then would be sent after Exit, as the admin (utils/viewAs.js).
  // Nor for an executive's account, which may only read (0098). Every write
  // throws ViewAsReadOnly then, and says so on screen; reading is never
  // refused, and housekeeping (prune) just waits.
  const guard = () => {
    const view = viewing();
    if (!view) return;
    announceReadOnly(view.role);
    throw readOnlyError(view);
  };
  const listeners = new Set();
  const changed = () => listeners.forEach((fn) => { try { fn(); } catch { /* a listener's own problem */ } });
  // One read-modify-write at a time per batch: the outbox saves its progress
  // while the editor may be saving a correction, and two writes built from
  // the same read would each undo the other.
  const locks = new Map();
  const serially = (id, work) => {
    const next = (locks.get(id) || Promise.resolve()).then(work, work);
    const tail = next.catch(() => {});
    locks.set(id, tail);
    tail.then(() => { if (locks.get(id) === tail) locks.delete(id); });
    return next;
  };

  let durableAsk = null;
  const store = {
    /** False when nothing written here outlives the page (no IndexedDB, or
     *  one that would not open). */
    get persistent() { return backend.persistent !== false; },

    /** Resolves once the backend knows whether it keeps anything, with
     *  `persistent`. */
    async ready() {
      if (backend.ready) await backend.ready().catch(() => {});
      return store.persistent;
    },

    /** Whether the browser may clear this store under storage pressure:
     *  true (it may not), false (it may) or null (cannot tell). Asks once per
     *  page for persistent storage, the first time something is kept: a
     *  phone full of field photographs is exactly when a browser evicts a
     *  site's data, and these photos exist nowhere else yet. */
    durable: null,
    async ensureDurable(storage = (typeof navigator !== 'undefined' ? navigator.storage : undefined)) {
      if (!store.persistent) return false;
      durableAsk = durableAsk || (async () => {
        try {
          if (!storage?.persisted || !storage?.persist) return null;
          if (await storage.persisted()) return true;
          return !!(await storage.persist());
        } catch {
          return null;
        }
      })();
      const answer = await durableAsk;
      if (store.durable !== answer) {
        store.durable = answer;
        changed();
      }
      return answer;
    },

    /** This person's batches, newest first. */
    async list(userId) {
      const all = await backend.getAll(BATCHES);
      return all.filter(b => b.userId === userId).sort((a, b) => order(b).localeCompare(order(a)));
    },

    async get(userId, id) {
      const batch = id ? await backend.get(BATCHES, id) : null;
      return batch && batch.userId === userId ? batch : null;
    },

    /** The camera's unfinished batch, if the person left one. */
    async active(userId) {
      return (await store.list(userId)).find(b => b.status === 'active') || null;
    },

    async save(batch) {
      guard();
      if (!batch?.id || !batch.userId) throw new Error('a batch needs an id and an owner');
      const stamped = { ...batch, updatedAt: new Date().toISOString() };
      await serially(batch.id, () => backend.put(BATCHES, stamped));
      changed();
      return stamped;
    },

    /**
     * Change one batch from what is stored now. `change` gets the current
     * batch and returns the next one, or null to leave it as it is. Answers
     * the stored batch, or null when it is gone or not this person's.
     */
    async update(userId, id, change) {
      guard();
      let wrote = false;
      const next = await serially(id, () => backend.update(BATCHES, id, (current) => {
        if (!current || current.userId !== userId) return { value: null };
        const proposed = change(current);
        if (!proposed) return { value: current };
        wrote = true;
        const stamped = { ...proposed, id, userId, updatedAt: new Date().toISOString() };
        return { put: stamped, value: stamped };
      }));
      if (wrote) changed();
      return next;
    },

    /** A batch and every photo and voice note it holds. */
    async remove(userId, id) {
      guard();
      const batch = await store.get(userId, id);
      if (!batch) return;
      await backend.remove(BLOBS, [...(batch.photos || []), ...(batch.audioNotes || [])].map(p => p.id));
      await serially(id, () => backend.remove(BATCHES, [id]));
      changed();
    },

    async putBlob(userId, id, blob) {
      guard();
      await backend.put(BLOBS, { id, userId, blob });
    },

    async getBlob(userId, id) {
      const entry = id ? await backend.get(BLOBS, id) : null;
      return entry && entry.userId === userId ? entry.blob : null;
    },

    async removeBlobs(userId, ids) {
      guard();
      const own = [];
      for (const id of ids || []) {
        const entry = await backend.get(BLOBS, id);
        if (entry && entry.userId === userId) own.push(id);
      }
      if (own.length) await backend.remove(BLOBS, own);
    },

    /** Batches the server has had in full for a while: it holds them now. */
    async prune(userId, { olderThanMs = 14 * 24 * 3600 * 1000, now = Date.now() } = {}) {
      if (viewing()) return;
      for (const batch of await store.list(userId)) {
        const sentAt = Date.parse(batch.sentAt || '');
        if (batch.status === 'sent' && Number.isFinite(sentAt) && now - sentAt > olderThanMs) {
          await store.remove(userId, batch.id);
        }
      }
    },

    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
  return store;
}

let shared = null;
/** The store the app uses: one per page, so every screen sees the same one. */
export function fieldStore() {
  shared = shared || createBatchStore();
  return shared;
}

/* ── batches, as data ─────────────────────────────────────────────────── */

export function newBatch({
  userId, source = 'camera', now = new Date(), missionId = null, targetId = null, observeOnly = false,
}) {
  return {
    id: newUuid(),
    userId,
    source,
    status: source === 'camera' ? 'active' : 'pending',
    attention: null,
    startedAt: null,
    location: null,
    // The field mission this device is out on (utils/activeMission.js): the
    // outbox sends it as POST /collections mission_id, and the server binds
    // the record only while the person still holds the card.
    missionId: missionId || null,
    // The observation this device was walking to when the batch began
    // (utils/activeTarget.js): sent as POST /collections target_sample_id,
    // and the server keeps it only for an observation the person may see.
    // A dropped pin has no id, and stamps nothing.
    targetId: targetId || null,
    photos: [],
    // Voice memos (newBatchAudio), their recordings in BLOBS beside the
    // photographs: the outbox sends them after the photos (fieldOutbox.js).
    audioNotes: [],
    // Field Mode's switch (utils/fieldMode.js) decides what a new batch
    // starts as; the batch keeps it, and the details form may change it.
    details: observeOnly ? { observeOnly: true } : {},
    createdAt: now.toISOString(),
  };
}

/** One voice memo: its type, length and when it was recorded. Its recording
 *  is put in the store under the same id (putBlob). */
export function newBatchAudio({ id, contentType, durationS = null, recordedAt = null }) {
  return {
    id: id || newUuid(),
    contentType,
    durationS: Number.isFinite(durationS) ? durationS : null,
    recordedAt: recordedAt || null,
    sent: false,
  };
}

/** A fix from the device ({lat, lng, accuracy_m, at}), if it is recent enough
 *  to say where the phone is now. */
export function freshFix(fix, now = Date.now(), maxAgeMs = FIX_MAX_AGE_MS) {
  if (!fix || !Number.isFinite(fix.lat) || !Number.isFinite(fix.lng)) return null;
  const at = Number(fix.at);
  return Number.isFinite(at) && now - at <= maxAgeMs ? fix : null;
}

/** One photograph: what it shows, and when and where it was taken. */
export function newBatchPhoto({ view, takenAt = null, fix = null, note, heading = null }) {
  return {
    id: newUuid(),
    view,
    takenAt: takenAt ? localIsoWithOffset(takenAt) : null,
    lat: fix ? fix.lat : null,
    lng: fix ? fix.lng : null,
    accuracy_m: fix && Number.isFinite(fix.accuracy_m) ? fix.accuracy_m : null,
    // The compass bearing the back camera faced (utils/camera/heading.js),
    // or null where the phone reported no orientation (0090).
    heading: Number.isFinite(heading) ? heading : null,
    ...(note ? { note } : {}),
    sent: false,
  };
}

/** The first of top, bottom and habitat this batch lacks; else an extra. */
export function nextView(photos) {
  const taken = new Set((photos || []).map(p => p.view));
  return MAIN_VIEWS.find(v => !taken.has(v)) || 'other';
}

/** The view after this one, for the chip that cycles them. */
export function cycleView(view) {
  const at = CAMERA_VIEWS.indexOf(view);
  return CAMERA_VIEWS[(at + 1) % CAMERA_VIEWS.length];
}

/** The most accurate fix among the photos (one with no stated accuracy counts
 *  as the least accurate), as a location. */
export function bestPhotoFix(photos) {
  let best = null;
  for (const p of photos || []) {
    if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) continue;
    const accuracy = Number.isFinite(p.accuracy_m) ? p.accuracy_m : Infinity;
    if (!best || accuracy < best.rank) best = { rank: accuracy, lat: p.lat, lng: p.lng, accuracy_m: p.accuracy_m ?? null };
  }
  return best ? { lat: best.lat, lng: best.lng, accuracy_m: best.accuracy_m } : null;
}

/**
 * The camera's batch, finished: dated by its first photo, placed by its most
 * accurate photo fix, else by where the phone is now. Neither may be known --
 * a batch with no place waits for one (see fieldOutbox.js).
 */
export function finalizeBatch(batch, currentFix = null, now = new Date()) {
  const photos = batch.photos || [];
  const here = freshFix(currentFix, now.getTime());
  const location = bestPhotoFix(photos)
    || (here ? { lat: here.lat, lng: here.lng, accuracy_m: Number.isFinite(here.accuracy_m) ? here.accuracy_m : null } : null);
  return {
    ...batch,
    status: 'pending',
    attention: null,
    startedAt: photos[0]?.takenAt || batch.startedAt || localIsoWithOffset(now),
    location,
    finalizedAt: now.toISOString(),
  };
}

/** The photo the gallery shows for a batch: its cap, else its first. */
export const coverPhoto = batch => (batch?.photos || []).find(p => p.view === 'cap_top') || batch?.photos?.[0] || null;

/**
 * A batch whose record is not on this server (never was, or was removed for
 * good), made ready to be sent again as a NEW record: everything the old
 * record's requests left on it goes -- a kept create or mint key would have
 * the server answer with the old record again. `onDevice` holds the ids of
 * the photos and voice notes whose files are still on the phone: those go
 * again (their storage keys named the old record); the rest are let go.
 * `keepCode` keeps the specimen code minted for it, when that code is still a
 * specimen's on this server (a typed label is always kept: it is in the
 * details).
 */
export function resendAsNew(batch, { keepCode = false, onDevice = new Set() } = {}) {
  const fresh = item => ({
    ...item, sent: false, skipped: false, s3_key: undefined, s3_url: undefined, thumb_key: undefined,
    tagsRefused: undefined, timeRefused: undefined,
  });
  const gone = item => ({ ...item, sent: false, skipped: true, lost: true });
  return {
    ...batch,
    status: 'pending',
    attention: null,
    gone: undefined,
    serverSampleId: undefined,
    serverCode: undefined,
    serverBase: undefined,
    createKey: undefined,
    createBody: undefined,
    sentBodies: undefined,
    pendingEdits: undefined,
    editsNotApplied: undefined,
    photoPasses: 0,
    serverErrors: 0,
    phenotypeSent: undefined,
    // The photographs go again under new server ids: so do their marks.
    annotationsDone: undefined,
    outlineSent: undefined,
    submitted: undefined,
    sentAt: undefined,
    ai: undefined,
    aiLinked: undefined,
    ...(keepCode ? {} : {
      specimenCode: undefined, specimenId: undefined, mintRequest: undefined, mintKey: undefined,
      mintRefused: undefined,
    }),
    photos: (batch.photos || []).map(p => (onDevice.has(p.id) && !p.lost ? fresh(p) : gone(p))),
    audioNotes: (batch.audioNotes || []).map(a => (onDevice.has(a.id) && !a.lost ? fresh(a) : gone(a))),
  };
}

/* ── the screen this replaced ─────────────────────────────────────────── */

export const LEGACY_DRAFT_KEY = UNOWNED_DRAFT_KEY;

const present = v => v !== null && v !== undefined && String(v).trim() !== '';
// What made the old screen offer a draft back: something entered, or a save
// already under way. The device's own location alone was not.
const LEGACY_ENTERED = ['qr_uid', 'current_taxon_name', 'current_rank', 'substrate', 'host_species',
  'general_notes', 'submitKey', 'serverSampleId', 'specimenCode'];
const phenotypeOf = raw => (raw?.phenotype && typeof raw.phenotype === 'object' && !Array.isArray(raw.phenotype)
  && Object.keys(raw.phenotype).length ? raw.phenotype : null);

/**
 * The quick-capture screen's unfinished capture (one draft in localStorage,
 * until 2026-10-06), as a batch -- or null when it held nothing worth
 * keeping. A half-saved one keeps what its save made (its code, its record,
 * its idempotency keys, its phenotype), so the outbox carries on from there
 * and never mints or creates twice. Its photos were never kept, so it has
 * none; nor is it dated or placed by this phone now.
 *
 * It waits for the signed-in person to look at it (`attention`,
 * 'legacy_draft') rather than going out at once: that draft carried no owner,
 * and on a shared phone the person who opens Field Mode first need not be
 * the one who left it.
 */
export function fromLegacyDraft(raw, userId, now = new Date()) {
  if (!raw || typeof raw !== 'object' || !userId) return null;
  const observeOnly = raw.observeOnly ?? raw.mode === 'observe';
  const phenotype = phenotypeOf(raw);
  const entered = LEGACY_ENTERED.some(k => present(raw[k])) || !!raw.locationByHand || !!raw.regionByHand
    || !!observeOnly || !!phenotype || !!raw.identified || !!raw.skippedToAi;
  if (!entered) return null;
  const began = raw.startedAt ? new Date(raw.startedAt) : null;
  const lat = Number(raw.gps_lat);
  const lng = Number(raw.gps_lng);
  const located = present(raw.gps_lat) && present(raw.gps_lng) && Number.isFinite(lat) && Number.isFinite(lng);
  return {
    ...newBatch({ userId, source: 'draft', now }),
    status: 'attention',
    attention: { reason: 'legacy_draft' },
    startedAt: localIsoWithOffset(began && Number.isFinite(began.getTime()) ? began : now),
    finalizedAt: now.toISOString(),
    location: located ? { lat, lng, accuracy_m: null } : null,
    ...(present(raw.region) ? { region: String(raw.region) } : {}),
    details: {
      taxon_name: raw.current_taxon_name || '',
      rank: raw.current_rank || '',
      substrate: raw.substrate || '',
      host_species: raw.host_species || '',
      general_notes: raw.general_notes || '',
      label: raw.qr_uid || '',
      observeOnly: !!observeOnly,
    },
    ...(raw.specimenCode ? { specimenCode: raw.specimenCode } : {}),
    ...(raw.mintRefused ? { mintRefused: true } : {}),
    // The old screen's one key, and its mint's, derived as it derived them.
    ...(raw.submitKey ? { createKey: raw.submitKey, mintKey: `${raw.submitKey}-mint` } : {}),
    ...(raw.mintRequest ? { mintRequest: raw.mintRequest } : {}),
    ...(raw.createBody ? { createBody: raw.createBody } : {}),
    ...(raw.serverSampleId ? { serverSampleId: raw.serverSampleId, serverCode: raw.serverCode } : {}),
    ...(phenotype ? { phenotype } : {}),
  };
}

/** Move that draft into the store, once. Answers the batch made, or null. */
export async function adoptLegacyDraft(store, userId, storage = globalThis.localStorage) {
  let text = null;
  try { text = storage?.getItem(LEGACY_DRAFT_KEY); } catch { return null; }
  if (!text) return null;
  let raw = null;
  try { raw = JSON.parse(text); } catch { /* unreadable: nothing to keep */ }
  // Decided first: a draft is let go only once there is a batch to hold what
  // it had, or it had nothing worth holding.
  const batch = fromLegacyDraft(raw, userId);
  // Taken off at once (before anything is awaited), so a second mount cannot
  // adopt it twice; put back if the store will not take it.
  try { storage.removeItem(LEGACY_DRAFT_KEY); } catch { return null; }
  if (!batch) return null;
  try {
    return await store.save(batch);
  } catch {
    try { storage.setItem(LEGACY_DRAFT_KEY, text); } catch { /* nothing more to do */ }
    return null;
  }
}
